import React, {
  useMemo,
  useState,
  useCallback,
  useEffect,
  useRef,
} from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { useHistory, useLocation } from 'react-router-dom';

import moment from 'moment-timezone';
import { Spinner } from 'reactstrap';
import { timelineActionTypes } from '@store/Timeline/Timeline.actionTypes';
import { useMemberCompanyTimezones } from '@hooks/useMemberCompanyTimezones';
import styles from './AppUsage.module.scss';
import { formatTimeFromSeconds } from '@/utils/timeUtils';
import { normalizeIdleForDisplay } from '@/utils/idleDisplayUtils';
import { Dropdown } from '@/components/Dropdown';
import { TimeFilter } from '@/components/TimeFilter/TimeFilter.component';
import { Table } from '@/components/Table';
import { appUsageTableConfig, AppUsageStats } from './AppUsage.config';
import { Accordion } from '@/components/Accordion';
import { fontWeightEnum, Text, textVariantsEnum } from '@/components/Text';
import { IoRefresh } from 'react-icons/io5';
import { useTeamMemberDetailsForSelect } from '@/hooks/useTeamMemberDetailsForSelect';
import { Button } from 'reactstrap';
import { SessionSourceTags } from '@/components/SessionSourceTags';
import {
  ProductivityScoreCard,
  TotalTimeTrackedCard,
  IdleTimeCard,
} from '@/components/StatsCards';
import { formatPercent } from '@/utils/numberUtils';
import {
  useDashboardOverviewUrlWrite,
  useReportFiltersPopAndMemberHydration,
} from '@/hooks/useDashboardOverviewUrlSync';
import { getInitialReportDateRangeFromSearch } from '@/utils/dashboardOverviewUrlFilters';
import { fetchApplicationInfoMapping } from '@services/applicationInfo';
import { getAppNameByAppPath } from '@/utils/utility';

export const AppUsageComponent = () => {
  const dispatch = useDispatch();
  const location = useLocation();
  const history = useHistory();
  const { sessionsByDay, loading, filters } = useSelector(
    (state) => state.timeline
  );
  const [selectedMember, setSelectedMember] = useState(null);

  const team = useSelector((state) => state.team);
  const auth = useSelector((state) => state.auth);
  // Get teamId and companyId for timezone hook
  const teamId = team?.active?.activeTeamid;
  const companyId = auth?.profile?.activeCompany?.companyId;
  const memberId = selectedMember?.value || null;
  const {
    memberDetails,
    isLoading: membersLoading,
    isError: membersError,
    error: membersErrorObj,
    refetch: refetchMembers,
  } = useTeamMemberDetailsForSelect(teamId);
  const membersOptions = useMemo(() => {
    if (!memberDetails || typeof memberDetails !== 'object') return [];
    return Object.entries(memberDetails)
      .filter(([, m]) => !m?.deleted)
      .map(([uid, m]) => ({
        value: uid,
        label: m.employeeId
          ? `${m.userName || uid} (${m.employeeId})`
          : m.userName || uid,
        employeeId: m.employeeId,
        rate: m.rates ?? 0,
      }))
      .sort((a, b) => (a.label || '').localeCompare(b.label || ''));
  }, [memberDetails]);

  const membersOptionsRef = useRef(membersOptions);
  membersOptionsRef.current = membersOptions;

  const [range, setRange] = useState(() =>
    getInitialReportDateRangeFromSearch(
      typeof window !== 'undefined' ? window.location.search : ''
    )
  );

  const {
    urlTimezoneForHook,
    userOverrideTimezoneRef,
    setIgnoreUrlTimezone,
    onMemberFilterChange,
  } = useReportFiltersPopAndMemberHydration({
    history,
    location,
    teamId,
    currentUserId: auth?.profile?.id,
    membersOptions,
    selectedMember,
    setSelectedMember,
    setDateRange: setRange,
    membersOptionsRef,
  });

  const {
    timezoneOptions,
    selectedTimezone,
    setSelectedTimezone,
    timezonesLoading,
  } = useMemberCompanyTimezones({
    teamId,
    memberId,
    companyId,
    initialTimezone: urlTimezoneForHook || filters?.timeZone || null,
    autoSelectMemberTimezone: true,
    userOverrideTimezoneRef,
  });

  useDashboardOverviewUrlWrite({
    teamId,
    memberId: selectedMember?.value,
    dateRange: range,
    selectedTimezone,
    history,
    location,
  });

  const handleTimezoneFilterChange = useCallback(
    (value) => {
      userOverrideTimezoneRef.current = true;
      setIgnoreUrlTimezone(true);
      setSelectedTimezone(value);
    },
    [setSelectedTimezone, setIgnoreUrlTimezone, userOverrideTimezoneRef]
  );

  const [expandedSessions, setExpandedSessions] = useState(new Set());
  const [appNameMapping, setAppNameMapping] = useState({});

  const getEmployeeIdForUser = (userId) => {
    if (!userId) return '';
    const match = membersOptions.find((m) => m.value === userId);
    if (match?.employeeId) return match.employeeId;
    try {
      const stored = JSON.parse(localStorage.getItem('members')) || [];
      const found = stored.find((m) => m.userUID === userId);
      if (found?.employeeId) return found.employeeId;
    } catch (e) {
      console.warn('Unable to parse members from localStorage', e);
    }
    return userId;
  };

  const getRateForUser = (userId) => {
    const match = membersOptions.find((m) => m.value === userId);
    return match?.rate || 0;
  };

  const fetchTimeline = () => {
    if (!selectedMember) return;
    const employeeId = getEmployeeIdForUser(selectedMember.value);
    dispatch({
      type: timelineActionTypes.GET_SESSIONS,
      payload: {
        teamId,
        userUID: selectedMember.value,
        companyId,
        employeeId,
        memberId: selectedMember.value,
        startDate: range.start,
        endDate: range.end,
        timeZone: selectedTimezone,
        page: 1,
        pageSize: 1000, // Large page size to get all sessions
        rate: getRateForUser(selectedMember.value),
      },
    });
  };

  useEffect(() => {
    fetchApplicationInfoMapping().then((mapping) => {
      setAppNameMapping(mapping);
    });
  }, []);

  useEffect(() => {
    if (selectedMember) {
      // Clear expanded sessions when filters change
      setExpandedSessions(new Set());
      fetchTimeline();
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [selectedMember, selectedTimezone, range.start, range.end]);

  // Toggle session expansion
  const toggleSession = (sessionId) => {
    setExpandedSessions((prev) => {
      const newSet = new Set(prev);
      if (newSet.has(sessionId)) {
        newSet.delete(sessionId);
      } else {
        newSet.add(sessionId);
      }
      return newSet;
    });
  };

  // Expand all sessions by default when they load
  useEffect(() => {
    if (!loading && sessionsByDay?.length > 0) {
      const allSessionIds = new Set();
      sessionsByDay.forEach((day) => {
        day.sessions.forEach((session) => {
          if (session.id) {
            allSessionIds.add(session.id);
          }
        });
      });
      setExpandedSessions(allSessionIds);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [loading, sessionsByDay]);

  // Process app usage for a session
  const getSessionAppUsage = useCallback(
    (session) => {
      // Use appUsage directly from session (available for both split and non-split sessions)
      const appUsage = session.appUsage || {};

      if (!appUsage || Object.keys(appUsage).length === 0) {
        return [];
      }

      return Object.entries(appUsage)
        .map(([appPath, seconds]) => ({
          appPath,
          appName: getAppNameByAppPath(appPath, appNameMapping),
          seconds: Number(seconds) || 0,
          formattedTime: formatTimeFromSeconds(Number(seconds) || 0),
        }))
        .filter((app) => app.seconds > 0) // Filter out zero time
        .sort((a, b) => b.seconds - a.seconds); // Sort by time descending
    },
    [appNameMapping]
  );

  return (
    <div className={styles.appUsagePage}>
      <div className={styles.filtersContainer}>
        <div className={styles.dropdownsContainer}>
          <div className={styles.dropdown}>
            <Dropdown
              options={membersOptions}
              value={selectedMember}
              label={'SELECT MEMBER'}
              onChange={onMemberFilterChange}
              isLoading={membersLoading}
              isDisabled={membersLoading || membersError}
            />
            {membersError && (
              <div className={styles.memberError}>
                <Text
                  variant={textVariantsEnum.Text_sm}
                  fontWeight={fontWeightEnum.Medium}
                >
                  Failed to load members. {membersErrorObj?.message || ''}
                </Text>
                <Button color="link" size="sm" onClick={() => refetchMembers()}>
                  Retry
                </Button>
              </div>
            )}
          </div>
          <div className={styles.dropdown}>
            <Dropdown
              label={'TIME'}
              options={timezoneOptions}
              value={
                timezoneOptions.find((t) => t.value === selectedTimezone) ||
                (selectedTimezone
                  ? { value: selectedTimezone, label: selectedTimezone }
                  : null)
              }
              onChange={({ value }) => handleTimezoneFilterChange(value)}
              isLoading={timezonesLoading}
            />
          </div>
        </div>
        <div className={styles.timeFilterContainer}>
          <TimeFilter range={range} setRange={setRange} />
        </div>
        <div className={styles.refreshButton} onClick={fetchTimeline}>
          <IoRefresh />
          <Text
            variant={textVariantsEnum.Text_md}
            fontWeight={fontWeightEnum.Medium}
          >
            Refresh
          </Text>
        </div>
      </div>
      <div className={styles.appUsage}>
        {loading && (
          <div className={styles.loaderContainer}>
            <Spinner color="primary" />
            <p className="mt-3 text-muted">Loading sessions...</p>
          </div>
        )}

        {!loading && !sessionsByDay.length && (
          <div className={'text-center text-muted my-5 w-100'}>
            No sessions found for selected filters.
          </div>
        )}
        {!loading &&
          sessionsByDay.length > 0 &&
          sessionsByDay.map((daySession) => {
            return (
              <Accordion
                contentClassName={styles.accordionContent}
                heading={
                  <div className={styles.accordionHeadingContainer}>
                    <Text
                      variant={textVariantsEnum.Display_xs}
                      fontWeight={fontWeightEnum.Medium}
                    >
                      {daySession.dayLabel}
                    </Text>
                    <hr className={styles.accordionHeadingDivider} />
                  </div>
                }
              >
                <div className={styles.statsWrapper}>
                  <ProductivityScoreCard
                    productivityScore={formatPercent(
                      daySession.totals.activityPct
                    )}
                    contaninerClassName={styles.statCard}
                  />
                  <TotalTimeTrackedCard
                    totalTimeTracked={formatTimeFromSeconds(
                      daySession.totals.durationSec
                    )}
                    contaninerClassName={styles.statCard}
                  />
                  <IdleTimeCard
                    idleTime={formatTimeFromSeconds(
                      normalizeIdleForDisplay(
                        daySession.totals.durationSec,
                        daySession.totals.idleSec
                      ).displayIdle
                    )}
                    contaninerClassName={styles.statCard}
                  />
                </div>
                {daySession.sessions.map((session) => {
                  const startTime = moment(session.start)
                    .tz(selectedTimezone)
                    .format('hh:mm A');
                  const endTime = session.end
                    ? moment(session.end).tz(selectedTimezone).format('hh:mm A')
                    : 'Ongoing';
                  const appUsageList = getSessionAppUsage(session);

                  return (
                    <Accordion
                      headingClass={styles.accordionHeading}
                      contentClassName={styles.accordionContentInner}
                      heading={
                        <div className={styles.sessionHeading}>
                          <Text
                            variant={textVariantsEnum.Display_xs}
                            fontWeight={fontWeightEnum.Medium}
                          >
                            {startTime} to {endTime}
                          </Text>
                          <SessionSourceTags
                            pastData={session?.pastData}
                            typeLog={session?.typeLog}
                          />
                        </div>
                      }
                    >
                      <Table
                        className={styles.appUsageTable}
                        data={appUsageList}
                        columns={appUsageTableConfig()}
                      />
                    </Accordion>
                  );
                })}
              </Accordion>
            );
          })}
      </div>
    </div>
  );
};
